import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { X } from "phosphor-react";
import React, { useEffect, useState } from "react";
import { fpost, fget } from "../../API/callsAPI";
import commonStyles from "../../Utils/commonStyles";
import CreateOrEditCustomFieldModal from "../Settings/FieldTemplatesSettings/CreateOrEditCustomFieldModal";
import fieldTemplatesStyles from "../Settings/FieldTemplatesSettings/FieldTemplatesStyles";
import SingleFieldView from "../Settings/FieldTemplatesSettings/SingleFieldView";
import SingleTableView from "../Settings/FieldTemplatesSettings/SingleTableView";
import CustomPageColumnsEdit from "./CustomPageColumnsEdit";
import CustomPageConditionsEdit from "./CustomPageConditionsEdit";

export default function CreateNewCustomPageModal({
  handleClose,
  projectId,
  refreshData,
}) {
  const commonClasses = commonStyles();
  const classes = fieldTemplatesStyles();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    pageName: "",
    description: "",
    trackType: "sequences",
  });

  const [selectedTrackable, setSelectedTrackable] = useState({
    id: null,
    name: null,
  });
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedPageFilter, setSelectedPageFilter] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedManualFieldsList, setSelectedManuaFieldsList] = useState([]);

  const steps = ["Basics", "Columns", "Conditions"];

  const handleNext = () => {
    if (currentStep === 2) {
      setIsLoading(true);
      handleCreateNewCustomPage({
        project: projectId,
        name: formData.pageName,
        description: formData.description,
        trackable: selectedTrackable.id,
        filter: selectedPageFilter,
      });
    } else if (currentStep === 1 && formData.trackType === "manual") {
      setIsLoading(true);
      handleCreateManualPage();
    }else{
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
    
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Api calls
  // 1. Create a new Custom Page
  // 2.  Update a Custom Page column and filter pattern

  const handleCreateNewCustomPage = async ({
    project,
    name,
    description,
    trackable,
    filter,
  }) => {
    // This page will make post api call to custom page with name, description, associated trackable and filters
    // Build pageConfig as {0: "asset", 1: "name", ...} where keys are the index of the column and values are the column name (or identifier if you prefer)
    const pageConfig = {};
    selectedColumns.forEach((eachItem, index) => {
      // Use the property you want here: "name", "identifier" or another. Using "name" for now as in your format example.
      pageConfig[index] = eachItem.name || eachItem.identifier;
    });

    const customPagePayload = {
      project: project,
      name: name,
      description: description,
      trackable: trackable,
      filter: filter,
      page_config: pageConfig,
    };
    try {
      const res = await fpost({
        url: "trackables/custom-page/",
        data: customPagePayload,
      });
      if (res.status === 200 || res.status === 201) {
        setIsLoading(false);
        refreshData();
        handleClose();
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const handleCreateManualPage = async () => {
   const fieldIds = selectedManualFieldsList.map((eachField) => eachField.id);
    const pageConfig = {};
    selectedManualFieldsList.forEach((eachItem, index) => {
      pageConfig[index] = eachItem.name || eachItem.identifier;
    });

    const customPagePayload = {
      project: projectId,
      name: formData.pageName,
      description: formData.description,
      trackable: "",
      filter: "",
      page_config: pageConfig,
      fields: fieldIds
    };
    try {
      const res = await fpost({
        url: "trackables/custom-page/",
        data: customPagePayload,
      });
      if (res.status === 200 || res.status === 201) {
        setIsLoading(false);
        refreshData();
        handleClose();
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <Box className={commonClasses.flexAndGap8Px}>
              <Box width="100%">
                <Typography>Page Name*</Typography>
                <Box
                  style={{ marginTop: "5px" }}
                  className={classes.cardContentDiv3}
                >
                  <TextField
                    placeholder="Enter page name here"
                    value={formData.pageName}
                    onChange={(e) =>
                      setFormData({ ...formData, pageName: e.target.value })
                    }
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "6px",
                        backgroundColor: "transparent",
                        color: "white !important",
                        opacity: 1,
                        fontSize: "16px",
                        fontFamily: "poppins",
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "transparent",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                        },
                      },
                    }}
                    fullWidth
                  />
                </Box>
              </Box>
              <Box width="100%">
                <Typography>Description</Typography>
                <Box
                  style={{ marginTop: "5px" }}
                  className={classes.cardContentDiv3}
                >
                  <TextField
                    placeholder="Enter a description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "6px",
                        backgroundColor: "transparent",
                        color: "white !important",
                        opacity: 1,
                        fontSize: "16px",
                        fontFamily: "poppins",
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "transparent",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                        },
                      },
                    }}
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography sx={{ mb: 2 }}>What does this page track?</Typography>
              <RadioGroup
                value={formData.trackType}
                onChange={(e) =>
                  setFormData({ ...formData, trackType: e.target.value })
                }
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "1fr 1fr",
                    },
                    gap: "12px",
                  }}
                >
                  <FormControlLabel
                    value="sequences"
                    control={
                      <Radio
                        sx={{
                          color: "#606479",
                          "&.Mui-checked": {
                            color: "#6C65D9",
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "white",
                          }}
                        >
                          Sequences & Shots
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "rgba(255,255,255,0.6)",
                          }}
                        >
                          Rows automatically fill in from sequences, shots &
                          shot variants
                        </Typography>
                      </Box>
                    }
                    sx={{
                      backgroundColor: "#252A38",
                      borderRadius: "10px",
                      border: `2px solid ${
                        formData.trackType === "sequences"
                          ? "#6C65D9"
                          : "transparent"
                      }`,
                      p: "12px",
                      m: 0,
                      transition: "all 0.2s",
                    }}
                  />
                  <FormControlLabel
                    value="assets"
                    control={
                      <Radio
                        sx={{
                          color: "#606479",
                          "&.Mui-checked": {
                            color: "#6C65D9",
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "white",
                          }}
                        >
                          Assets
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "rgba(255,255,255,0.6)",
                          }}
                        >
                          Rows automatically fill in from assets & asset
                          versions
                        </Typography>
                      </Box>
                    }
                    sx={{
                      backgroundColor: "#252A38",
                      borderRadius: "10px",
                      border: `2px solid ${
                        formData.trackType === "assets"
                          ? "#6C65D9"
                          : "transparent"
                      }`,
                      p: "12px",
                      m: 0,
                      transition: "all 0.2s",
                    }}
                  />
                  <FormControlLabel
                    value="tasks"
                    control={
                      <Radio
                        sx={{
                          color: "#606479",
                          "&.Mui-checked": {
                            color: "#6C65D9",
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "white",
                          }}
                        >
                          Tasks
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "rgba(255,255,255,0.6)",
                          }}
                        >
                          Rows automatically fill in from tasks.
                        </Typography>
                      </Box>
                    }
                    sx={{
                      backgroundColor: "#252A38",
                      borderRadius: "10px",
                      border: `2px solid ${
                        formData.trackType === "tasks"
                          ? "#6C65D9"
                          : "transparent"
                      }`,
                      p: "12px",
                      m: 0,
                      transition: "all 0.2s",
                    }}
                  />
                  <FormControlLabel
                    value="manual"
                    control={
                      <Radio
                        sx={{
                          color: "#606479",
                          "&.Mui-checked": {
                            color: "#6C65D9",
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "white",
                          }}
                        >
                          Manual Table
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "rgba(255,255,255,0.6)",
                          }}
                        >
                          No auto-population — you define columns and enter all
                          data yourself. Skip to column setup.
                        </Typography>
                      </Box>
                    }
                    sx={{
                      backgroundColor: "#252A38",
                      borderRadius: "10px",
                      border: `2px solid ${
                        formData.trackType === "manual"
                          ? "#6C65D9"
                          : "transparent"
                      }`,
                      p: "12px",
                      m: 0,
                      transition: "all 0.2s",
                    }}
                  />
                </Box>
              </RadioGroup>
            </Box>
          </Box>
        );
      case 1:
        if (formData.trackType === "manual") {
          return (
            <ManualPageColumns
              projectId={projectId}
              selectedManualFieldsList={selectedManualFieldsList}
              setSelectedManuaFieldsList={setSelectedManuaFieldsList}
            />
          );
        }
        return (
          <CustomPageColumnsEdit
            projectId={projectId}
            trackType={formData.trackType}
            onSelectColumns={(columns) => setSelectedColumns(columns)}
            selectedTrackable={selectedTrackable}
            setSelectedTrackable={setSelectedTrackable}
          />
        );
      case 2:
        return (
          <CustomPageConditionsEdit
            projectId={projectId}
            selectedTrackable={selectedTrackable}
            trackType={selectedTrackable.name}
            trackableId={selectedTrackable.id}
            onFilterChange={(filterData) => {
              console.log("Filtered Data:", filterData);
              setSelectedPageFilter(filterData);
            }}
          />
        );
      case 3:
        return <Box>Access Step Content</Box>;
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        width: "80%",
        maxWidth: "90vw",
        maxHeight: "85vh",
        backgroundColor: "#1B1D28",
        color: "white",
        borderRadius: "20px",
        padding: "10px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box className={commonClasses.flexBetween}>
          <Typography sx={{ fontSize: "20px !important", fontWeight: 600 }}>
            New Page
          </Typography>
          <Button
            sx={{ padding: 0, minWidth: 0, color: "white" }}
            onClick={handleClose}
          >
            <X size={20} />
          </Button>
        </Box>

        <Stepper
          activeStep={currentStep}
          sx={{
            "& .MuiStepLabel-root": {
              padding: { xs: "0 4px", sm: "0 8px" },
            },
            "& .MuiStepLabel-label": {
              color: "#606479",
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 400,
              "&.Mui-active": {
                color: "white",
                fontWeight: 500,
              },
              "&.Mui-completed": {
                color: "#606479",
              },
            },
            "& .MuiStepIcon-root": {
              color: "#606479",
              "&.Mui-active": {
                color: "#6C65D9",
              },
              "&.Mui-completed": {
                color: "#6C65D9",
              },
            },
            "& .MuiStepConnector-line": {
              borderColor: "#606479",
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#606479",
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: "#707489",
              },
            },
          }}
        >
          {renderStepContent()}
        </Box>

        <Box className={commonClasses.flexBetween} sx={{ pt: 2 }}>
          {currentStep > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={isLoading}
              sx={{
                borderColor: "#606479",
                color: "white",
                borderRadius: "10px",
                textTransform: "none",
                px: 4,
                py: 1,
                "&:hover": {
                  borderColor: "#707489",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
                "&.Mui-disabled": {
                  borderColor: "#606479",
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              <Typography>Back</Typography>
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (currentStep === 0 && formData.pageName.trim() === "") ||
              isLoading
            }
            sx={{
              backgroundColor: "#6C65D9",
              borderRadius: "10px",
              textTransform: "none",
              px: 4,
              py: 1,
              marginLeft: currentStep === 0 ? "auto" : 0,
              "&:hover": {
                backgroundColor: "#5A55C7",
              },
              "&.Mui-disabled": {
                backgroundColor: "#606479",
                color: "rgba(255, 255, 255, 0.5)",
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              <Typography>
                {currentStep === 1 && formData.trackType === "manual"
                  ? "Create Page"
                  : "Next"}
              </Typography>
            )}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

const ManualPageColumns = ({
  projectId,
  setSelectedManuaFieldsList,
  selectedManualFieldsList,
}) => {
  const commonClasses = commonStyles();
  const [isCreateNewFieldModalOpen, setIsCreateNewFieldModalOpen] =
    useState(false);
  const [customFields, setCustomFields] = useState([]);

  const handleFieldClick = (field) => {
    if (selectedManualFieldsList?.length) {
      const isExist = selectedManualFieldsList?.some(
        (sin) => sin.name === field.name,
      );
      if (isExist) {
        setSelectedManuaFieldsList(
          selectedManualFieldsList.filter((sin) => sin.name !== field.name),
        );
      } else {
        setSelectedManuaFieldsList([...selectedManualFieldsList, field]);
      }
    } else {
      setSelectedManuaFieldsList([field]);
    }
  };

  const handleGetTemplates = async () => {
    try {
      let url = `trackables/trackable-field/?project=${projectId}`;
      const res = await fget({
        url: url,
      });

      setCustomFields(res?.data?.custom_fields);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    handleGetTemplates();
  }, []);

  return (
    <>
      <Box>
        <SingleFieldView
          title="Custom Fields"
          showAddIcon={true}
          handleAddNew={() => setIsCreateNewFieldModalOpen(true)}
          fields={customFields}
          type="custom"
          onFieldClick={handleFieldClick}
          selectedFields={selectedManualFieldsList}
          handleGetTemplates={handleGetTemplates}
        />
      </Box>
      <SingleTableView items={selectedManualFieldsList ?? []} onReorder={(newOrder) => setSelectedManuaFieldsList(newOrder)} />
      <Modal
        open={isCreateNewFieldModalOpen}
        onClose={() => setIsCreateNewFieldModalOpen(false)}
        className={commonClasses.flexCenter}
      >
        <CreateOrEditCustomFieldModal
          type="new"
          handleClose={() => setIsCreateNewFieldModalOpen(false)}
          projectId={projectId}
          updateRefreshCounter={handleGetTemplates}
          selectedTrackable={null}
        />
      </Modal>
    </>
  );
};
